import { useState } from 'react';

import { message } from 'antd';

import {
  AdminFonctionnelControllerApi,
  UploadDocumentSpecifiqueUsingPOSTQueryParams,
  UploadDocumentSpecifiqueViolationDtoFormFieldEnum,
} from 'lib_api/lib/api/gen';

import { useApi } from 'hooks/ApiStoreContext';
import { backAlertMessage } from 'hooks/utils/backAlertMessage';
import { ShouldNotHappen } from 'utils/exceptions';

import {
  DocumentSpecifiqueFormField,
  DocumentSpecifiqueFormFieldEnumType,
  DocumentSpecifiqueFormValue,
  SubmitFunction,
} from './type';

export function useSubmitUploadDocumentSpecifique(
  closeModal: () => void,
  onSuccess: (lastModificationDate: number) => void,
): SubmitFunction {
  const { AdminFonctionnelControllerApi: adminController } = useApi();

  const [inProgress, setInProgress] = useState<boolean>(false);

  return {
    submit: (values: DocumentSpecifiqueFormValue) => {
      setInProgress(true);

      const { file, ...queryParam } = values;

      return adminController
        .uploadDocumentSpecifiqueUsingPOST(
          file,
          computeBackAcceptedQueryParams(queryParam),
        )
        .then(
          () =>
            void message.success(
              `Le document spécifique a bien été enregistré.`,
            ),
        )
        .then(() => onSuccess(Date.now()))
        .then(closeModal)
        .catch((error: Response) => {
          void backAlertMessage(error);
        })
        .finally(() => {
          setInProgress(false);
        });
    },
    inProgress: inProgress,
  };
}

export function useValidateUploadDocumentSpecifique(): (
  field: DocumentSpecifiqueFormFieldEnumType,
  formValues: DocumentSpecifiqueFormValue,
) => Promise<void> {
  const { AdminFonctionnelControllerApi: adminController } = useApi();

  return async (
    field: DocumentSpecifiqueFormFieldEnumType,
    formValues: DocumentSpecifiqueFormValue,
  ) => {
    switch (field) {
      case DocumentSpecifiqueFormField.NAME:
        validateRequiredField('Nom du fichier', formValues.nomDocument);
        break;
      case DocumentSpecifiqueFormField.TYPE_DESTINATAIRE:
        validateRequiredArrayField(
          'destinataire',
          formValues.utilisateursDestinataires,
        );
        break;
      case DocumentSpecifiqueFormField.FILE:
        validateRequiredField('Fichier', formValues.file);
        break;
      default:
        throw new ShouldNotHappen(field);
    }

    // we don't want to call the back OR display an error if one of the required field is still null
    if (
      formValues.nomDocument &&
      formValues.file &&
      formValues.utilisateursDestinataires?.length
    ) {
      return backOfficeValidationHandle(
        adminController,
        formValues,
        field,
      ).catch(async e => {
        if (e instanceof Response) {
          return Promise.reject((await backAlertMessage(e)).description);
        }
        return Promise.reject(String(e));
      });
    }
  };
}

function validateRequiredArrayField<T>(fieldName: string, value?: T[]) {
  if (!value || value.length === 0) {
    throw `Le champ ${fieldName} est manquant`;
  }
}

function validateRequiredField<T>(fieldName: string, value?: T) {
  if (!value) {
    throw `Le champ ${fieldName} est manquant`;
  }
}

function backOfficeValidationHandle(
  adminController: AdminFonctionnelControllerApi,
  formValues: DocumentSpecifiqueFormValue,
  field: UploadDocumentSpecifiqueViolationDtoFormFieldEnum,
) {
  return adminController
    .validateUploadDocumentSpecifiqueFieldUsingPOST(
      formValues.file,
      field,
      computeBackAcceptedQueryParams(formValues),
    )
    .then(response => {
      const violations =
        response.uploadDocumentSpecifiqueViolationDtoList ?? [];

      if (violations.length === 0) {
        return Promise.resolve();
      }

      return Promise.reject(violations[0].message);
    });
}

const computeBackAcceptedQueryParams = (
  formValues: Partial<UploadDocumentSpecifiqueUsingPOSTQueryParams>,
): UploadDocumentSpecifiqueUsingPOSTQueryParams => {
  const { nomDocument, utilisateursDestinataires } = formValues;

  return {
    nomDocument: nomDocument ?? '',
    utilisateursDestinataires: utilisateursDestinataires ?? [],
  };
};
